import { memo, InputHTMLAttributes } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import classnames from 'classnames';
import { Theme, Colors } from '@app/theme';
import { TooltipText, Chip } from '@app/core';
import { RawCheckbox } from '../RawCheckbox';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.marginBase * 3 + 'px',

    width: '100%',
    padding: theme.marginBase * 3,
    paddingRight: theme.marginBase * 5,

    borderRadius: theme.borderRadius.std,
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: theme.colors.grayBorder,
    '&:hover': {
      backgroundColor: theme.colors.grayBackground,
    },
  },
  labelAndTextsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase + 'px',
  },
  title: {
    ...theme.fonts.h5,
  },
  text: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
  },
  containerChecked: {
    backgroundColor: theme.colors.grayBackground,
  },
  containerDisabled: {
    opacity: 0.5,
    cursor: 'auto',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  radio: {
    display: 'none',
  },
  icon: {
    color: theme.colors.green,
    position: 'absolute',
    top: theme.marginBase * 2,
    right: theme.marginBase * 2,
    fontSize: theme.iconSize.large,
  },
  centerChip: {
    width: 'fit-content',
  },
  illustration: {
    width: '100%',
    height: '100%',
    maxHeight: theme.marginBase * 8,
    maxWidth: theme.marginBase * 8,
  },
  topChip: {
    position: 'absolute',
    top: theme.marginBase * 2,
    right: theme.marginBase * 2,
  },
  mobileContainer: {
    padding: theme.marginBase * 2,
    marginBottom: theme.marginBase * 3,
  },
}));

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  illustration?: string;
  title: string;
  description?: string;
  checked?: boolean;
  disabled?: boolean;
  tooltip?: string;
  horizontal?: boolean;
  centerChip?: {
    label: string;
    color: Colors | undefined;
  };
  topChip?: {
    label: string;
    color: Colors | undefined;
    position?: 'center' | 'right';
  };
  trackingName?: string;
}

export const HorizontalTextAndImageSelectionBlock = memo(
  ({
    illustration,
    title,
    description,
    checked,
    disabled,
    tooltip,
    className,
    centerChip,
    topChip,
    horizontal = false,
    ...rest
  }: Props) => {
    const theme: Theme = useTheme();
    const classes = useStyles({ theme }) as any;

    return (
      <TooltipText title={tooltip} placement="top">
        <>
          <label
            htmlFor={`checkbox-${title}` as string}
            className={classnames(classes.container, className, {
              [classes.containerChecked]: checked,
              [classes.containerDisabled]: disabled,
              [classes.mobileContainer]: horizontal,
            })}
          >
            {topChip && (
              <Chip color={topChip.color} className={classes.topChip}>
                {topChip.label}
              </Chip>
            )}
            {checked && (
              <i
                className={classnames('ri-checkbox-circle-fill', classes.icon)}
              />
            )}

            {illustration && (
              <img
                src={illustration}
                alt={title}
                className={classes.illustration}
              />
            )}

            <div className={classes.labelAndTextsContainer}>
              {centerChip && (
                <Chip color={centerChip.color} className={classes.centerChip}>
                  {centerChip.label}
                </Chip>
              )}
              <div className={classes.title} aria-label="Label">
                {title}
              </div>
              {description && <p className={classes.text}>{description}</p>}
            </div>
          </label>

          <RawCheckbox
            id={`checkbox-${title}`}
            type="radio"
            className={classes.radio}
            noLabel
            data-tracking-name={rest.trackingName}
            {...rest}
          />
        </>
      </TooltipText>
    );
  },
);
